import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import { FunctionComponent, useState } from "react";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../ui/pages/content_panel/ui/new_components/common/loading_spinner";
import StadiumButton from "../../../../ui/new_components/common/stadium_button";
import { urls } from "../../../../utils/urls";
import { BOMItemModel, NPDModel } from "../../models/npd_model";
import { fetchGet, toQueryString } from "../../../../service/api_client";

interface ExportNPDProps {
  filter: {
    npd_id?: string;
    project?: string;
    product?: string;
    type?: string[];
    status?: string[];
    inward_status?: string[];
    page: number;
    count: number;
    sort_order: string;
    all?: any;
    fromDate?: string;
    toDate?: string;
  };
}

const ExportNPD: FunctionComponent<ExportNPDProps> = (props) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NPDModel[]>([]);
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd`;

  const getNPDDetailedListData = async () => {
    const filter = props.filter;
    setLoading(true);
    try {
      const searchProp = { ...filter };
      searchProp.all = true;
      const res = await fetchGet(url + toQueryString(searchProp));
      if (res.success) {
        console.log(res.data.data);
        if (res.data.data.length === 0) {
          throw new Error("No data found");
        }
        setData(res.data.data);
        if (res.data) {
          const options = {
            filename: `NPD Detailed Report`,
            fieldSeparator: ",",
            quoteStrings: '"',
            decimalSeparator: ".",
            showLabels: true,
            showTitle: false,
            title: "NPD Detailed Report",
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true,
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(
            res.data.data.flatMap((npd_data: NPDModel) => {
              const bom_items = npd_data.bom_items || [];

              // If no BOM items, return a single row with "--" for item-related fields
              if (bom_items.length === 0) {
                return [
                  {
                    "NPD ID": npd_data.id ?? "--",
                    Project: npd_data.project?.name ?? "--",
                    Product: npd_data.product?.name ?? "--",
                    "Created Date": moment(
                      npd_data.created_at,
                      moment.ISO_8601,
                    ).format("DD-MM-YYYY"),
                    "Created By": npd_data.created_by_name ?? "--",
                    Type: npd_data?.type ?? "--",
                    "Item Name": "--",
                    "Item Description": "--",
                    "Item Qty": "--",
                    Warehouse: "--",
                    "NPD Qty": npd_data.qty ?? "--",
                    "NPD Status": npd_data.status ?? "--",
                    "Inward Status": npd_data.inward_status ?? "--",
                    Attachment: npd_data.attachments?.length ?? 0,
                  },
                ];
              }

              // Return one row per BOM item
              return bom_items.map((item: BOMItemModel) => ({
                "NPD ID": npd_data.id ?? "--",
                Project: npd_data.project?.name ?? "--",
                Product: npd_data.product?.name ?? "--",
                "Created Date": moment(
                  npd_data.created_at,
                  moment.ISO_8601,
                ).format("DD-MM-YYYY"),
                "Created By": npd_data.created_by_name ?? "--",
                Type: npd_data?.type ?? "--",
                "Item Name": item.item?.name ?? "--",
                "Item Description": item.item?.description ?? "--",
                "Item Qty": item.qty ?? 0,
                Warehouse: item.warehouse?.name ?? "--",
                "NPD Qty": npd_data.qty ?? "--",
                "NPD Status": npd_data.status ?? "--",
                "Inward Status": npd_data.inward_status ?? "--",
                Attachment: npd_data.attachments?.length ?? 0,
              }));
            }),
          );
        }
      } else {
        console.log(res.error);
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      console.log("This ran first");
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  const getNPDSummarisedListData = async () => {
    const filter = props.filter;
    setLoading(true);
    try {
      const searchProp = { ...filter };
      searchProp.all = true;
      const res = await fetchGet(url + toQueryString(searchProp));
      if (res.success) {
        console.log(res.data.data);
        if (res.data.data.length === 0) {
          throw new Error("No data found");
        }
        setData(res.data.data);
        if (res.data) {
          const options = {
            filename: `NPD Summerised Report`,
            fieldSeparator: ",",
            quoteStrings: '"',
            decimalSeparator: ".",
            showLabels: true,
            showTitle: false,
            title: "NPD Summerised Report",
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true,
          };

          const csvExporter = new ExportToCsv(options);

          csvExporter.generateCsv(
            res.data.data.map((data: NPDModel, i: number) => {
              return {
                "NPD ID": data.id,
                Project: data.project?.name ?? "--",
                Product: data?.product?.name ?? "--",
                "Created At": moment(data.created_at, moment.ISO_8601).format(
                  "DD-MM-YYYY",
                ),
                "Created By": data.created_by_name ?? "--",
                Type: data.type ?? "--",
                "BOM Item":
                  data.bom_items.length > 0
                    ? `=HYPERLINK("https://${window.location.host}${
                        urls.purchase_order + "/view/" + data.id
                      }", "${data.bom_items[0].item.name}${
                        data.bom_items.length > 1
                          ? `... and ${data.bom_items.length - 1} more`
                          : ""
                      }")`
                    : "--",

                "NPD Status": data.status ?? "--",
                "Inward Status": data.inward_status ?? "--",
                Attachment: data.attachments.length,
              };
            }),
          );
        }
      } else {
        console.log(res.error);
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  return (
    <div className="p-5">
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center my-4">
        <StadiumButton type="button" onTap={getNPDDetailedListData}>
          Detailed Export
        </StadiumButton>
        <StadiumButton type="button" onTap={getNPDSummarisedListData}>
          Summarised Export
        </StadiumButton>
      </div>

      <LoadingWidget loading={loading} />
    </div>
  );
};

export default ExportNPD;
